import { section } from "@/glow";
import { listen, qsa, qs } from "@fluorescent/dom";
import accordions from "@/lib/accordions";
import { animateProductTabs, shouldAnimate } from "@/lib/animation";
import { slideUp, slideStop, isVisible } from "slide-anim";

const selectors = {
  tabLabels: "[data-tab-label]",
  tabItems: "[data-tab-item]",
  tabList: "[data-tab-list]",
  activeTabItem: "[data-tab-item][aria-hidden='false']",
  mobileAccordion: ".product-tabs__mobile-accordions",
  mobileLabel: ".accordion__label",
  header: "[data-section-type='header']",
};

section("product-tabs", {
  onLoad() {
    this.accordions = [];
    this.tabItems = qsa(selectors.tabItems, this.container);
    this.tabLabels = qsa(selectors.tabLabels, this.container);
    this.tabList = qs(selectors.tabList, this.container);

    this.activeTabItem = qs(selectors.activeTabItem, this.container);
    if (this.activeTabItem) {
      this._setTabHeight(this.activeTabItem);
    }

    this.clickHandlers = listen(this.tabLabels, "click", e => {
      e.preventDefault();

      const contentID = e.currentTarget.getAttribute("aria-controls");
      const content = qs(`#${contentID}`, this.container);
      this._closeAll();
      this._open(e.currentTarget, content);
    });

    const accordionElements = qsa(".accordion", this.container);
    accordionElements.forEach(accordion => {
      const accordionOpen = accordion.classList.contains("accordion--open");
      this.accordions.push(accordions(accordion, { firstOpen: accordionOpen }));
      accordion.classList.add("rte--product", "accordion--product");
    });

    if (shouldAnimate(this.container)) {
      this.animateProductTabs = animateProductTabs(this.container);
    }

    // Header info for auto-collapse rows
    this.header = qs(selectors.header, document);
    this.isStickyHeader = this.header?.dataset.enableStickyHeader === "true";
    this.headerHeight = 0;
    this.announcementHeight = 0;

    // Auto-collapse rows
    this.mobileAccordion = qs(selectors.mobileAccordion, this.container);
    this.shouldAutoCollapse =
      this.mobileAccordion.dataset.autoCollapse === "true";

    if (this.shouldAutoCollapse) {
      this._mobileAutoCollapse();
    }
  },

  _closeAll() {
    this.tabLabels.forEach(label => {
      const contentID = label.getAttribute("aria-controls");
      const content = qs(`#${contentID}`, this.container);
      if (this._isVisible(content)) {
        this._close(label, content);
      }
    });
  },

  _open(label, content) {
    label.setAttribute("aria-expanded", true);
    content.setAttribute("aria-hidden", false);
    this._setTabHeight(content);
  },

  _close(label, content) {
    label.setAttribute("aria-expanded", false);
    content.setAttribute("aria-hidden", true);
  },

  _isVisible(content) {
    return content.getAttribute("aria-hidden") === "false";
  },

  _setTabHeight(content) {
    const height = content.offsetHeight;
    this.tabList.style.height = `${height}px`;
  },

  // mobile accordion
  _mobileAutoCollapse() {
    this.mobileAccordion = qs(selectors.mobileAccordion, this.container);
    if (!this.mobileAccordion) return;

    this.mobileLabels = qsa(selectors.mobileLabel, this.mobileAccordion);
    if (!this.mobileLabels.length) return;

    this.mobileAutoCollapseClick = listen(this.mobileLabels, "click", event => {
      const label = event.currentTarget;
      const group = label.parentNode;
      const content = label.nextElementSibling;
      if (!content || isVisible(content)) return;

      this._closeOpenMobileRows(group);

      setTimeout(() => {
        const inViewData = this._isInView(label);

        if (!inViewData.inView) {
          window.scrollTo({
            top:
              window.scrollY +
              inViewData.elementTop -
              (this.headerHeight + this.announcementHeight),
            behavior: "smooth",
          });
        }
      }, 400);
    });
  },

  _closeOpenMobileRows(currentGroup) {
    for (const label of this.mobileLabels) {
      const group = label.parentNode;
      // TODO: silencing error. rework or consider ruleset tweak?
      // eslint-disable-next-line no-continue
      if (group === currentGroup) continue;

      const content = label.nextElementSibling;
      if (content && isVisible(content)) {
        slideStop(content);
        slideUp(content);
        group.setAttribute("data-open", false);
        label.setAttribute("aria-expanded", false);
        content.setAttribute("aria-hidden", true);
      }
    }
  },

  _isInView(element) {
    if (this.isStickyHeader) {
      this.headerHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--height-header")
          .replace(/px/gi, "")
      );

      this.announcementHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--announcement-height")
          .replace(/px/gi, "")
      );
    }

    const rect = element.getBoundingClientRect();

    return {
      inView:
        rect.top >= this.headerHeight + this.announcementHeight &&
        rect.bottom <= window.innerHeight,
      elementTop: rect.top,
    };
  },

  onBlockSelect({ target }) {
    const contentID = target.getAttribute("aria-controls");
    const content = qs(`#${contentID}`, this.container);

    this._closeAll();
    this._open(target, content);
  },

  onUnload() {
    this.clickHandlers();
    this.accordions.forEach(accordion => accordion.unload());
    this.animateProductTabs?.destroy();
    this.mobileAutoCollapseClick?.();
  },
});
